import { createBrowserHistory } from 'history';
import { ThunkAction, Action, combineReducers, configureStore } from '@reduxjs/toolkit';
import { createReduxHistoryContext, RouterState } from 'redux-first-history';
import logger from 'redux-logger';
import authReducer from '../features/auth/AuthSlice';
import usersReducer from '../features/users/UsersSlice';
import userReducer from '../features/users/user/UserSlice';
import paginationReducer from '../features/pagination/PaginationSlice';
import errorHandlerReducer from '../features/error/ErrorHandlerSlice';
import searchReducer from '../features/search/SearchSlice';
import zendeskReducer from '../features/zendesk/ZendeskSlice';
import urlsReducer from '../features/urls/UrlsSlice';
import deleteUrlsReducer from '../features/urls/deleteUrls/DeleteUrlsSlice';
import deleteUrlsModalReducer from '../features/urls/deleteUrls/deleteUrlsModal/DeleteUrlsModalSlice';
import bulkDeleteUrlsReducer from '../features/urls/bulkDeleteUrls/BulkDeleteUrlsSlice';
import bulkDeleteUrlsModalReducer from '../features/urls/bulkDeleteUrls/bulkDeleteUrlsModal/BulkDeleteUrlsModalSlice';
import userUpdateModalReducer from '../features/users/user/userUpdateModal/UserUpdateModalSlice';
import anonymizeUserModalReducer from '../features/users/user/anonymizeUserModal/AnonymizeUserModalSlice';
import seedRendersModalReducer from '../features/users/user/seedRendersModal/SeedRendersModalSlice';
import anonymizeLoginUserModalReducer from '../features/loginUsers/anonymizeLoginUserModal/AnonymizeLoginUserModalSlice';
import moveLoginUserModalReducer from '../features/loginUsers/moveLoginUserModal/MoveLoginUserModalSlice';
import ignoredUrlsReducer from '../features/users/user/ignoredUrlsTab/IgnoredUrlsSlice';
import loginUsersListReducer from '../features/loginUsers/LoginUsersListSlice';
import api from '../api/apiSlice';

const { routerMiddleware, routerReducer } = createReduxHistoryContext({
  history: createBrowserHistory(),
  savePreviousLocations: 3,
});

const isDev = !process.env.NODE_ENV || process.env.NODE_ENV === 'development';

export const store = configureStore({
  reducer: combineReducers({
    router: routerReducer,
    auth: authReducer,
    users: usersReducer,
    user: userReducer,
    pagination: paginationReducer,
    errorHandler: errorHandlerReducer,
    search: searchReducer,
    zendesk: zendeskReducer,
    urls: urlsReducer,
    deleteUrls: deleteUrlsReducer,
    deleteUrlsModal: deleteUrlsModalReducer,
    bulkDeleteUrls: bulkDeleteUrlsReducer,
    bulkDeleteUrlsModal: bulkDeleteUrlsModalReducer,
    userUpdateModal: userUpdateModalReducer,
    anonymizeUserModal: anonymizeUserModalReducer,
    seedRendersModal: seedRendersModalReducer,
    anonymizeLoginUserModal: anonymizeLoginUserModalReducer,
    moveLoginUserModal: moveLoginUserModalReducer,
    ignoredUrls: ignoredUrlsReducer,
    loginUsersList: loginUsersListReducer,
    [api.reducerPath]: api.reducer,
  }),
  middleware: (getDefaultMiddleware) => {
    const extraMiddlewares = [routerMiddleware];
    if (isDev) extraMiddlewares.push(logger as unknown as typeof routerMiddleware);

    return getDefaultMiddleware().concat(extraMiddlewares).concat(api.middleware);
  },
});

export const routerState = (state: RootState): RouterState | null => state.router;

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, Action<string>>;
